import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL } from "../config/apiconfig";


const API_URL = `${API_BASE_URL}/api`;

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userType, setUserType] = useState(null);
  const [companyId, setCompanyId] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const storedUserType = localStorage.getItem('userType');
    const storedCompanyId = localStorage.getItem('companyId');
    
    if (token && storedUserType) {
      // Only set companyId if it exists, otherwise it will be set after profile fetch
      if (storedCompanyId) {
        setCompanyId(storedCompanyId);
      }
      fetchUserProfile(token, storedUserType);
    } else {
      setLoading(false);
    }
  }, []);

  const fetchUserProfile = async (token, type) => {
    try {
      setLoading(true);
      setError(null);
      
      let response;
      const headers = {
        'Authorization': `Bearer ${token}`,
        'user-id': token,
        'user-type': type
      };
      
      if (type === 'employee') {
        response = await axios.get(`${API_URL}/employees/${token}`, { headers });
      } else {
        response = await axios.get(`${API_URL}/auth/user`, { headers });
      }
      
      if (!response.data) {
        throw new Error('Invalid response data');
      }

      setUser(response.data);
      setUserType(type);
      // Ensure companyId is set from the response if not already set
      if (response.data.company_id) {
        setCompanyId(response.data.company_id);
        localStorage.setItem('companyId', response.data.company_id);
      }
    } catch (err) {
      console.error('Failed to fetch user profile:', err);
      // Don't logout for network errors, only for authentication failures
      if (err.response && err.response.status === 401) {
        setError('Session expired. Please login again.');
        logout();
      } else {
        setError('Failed to fetch user data. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password, type = 'auto') => {
    try {
      setLoading(true);

      const endpoint = type === 'auto' ? 'auth/login-auto' : (type === 'user' ? 'auth/login' : 'auth/employee-login');
      const response = await axios.post(`${API_URL}/${endpoint}`, {
        email,
        password
      });

      const resolvedType = response.data?.userType || (type === 'auto' ? null : type);
      if (!resolvedType) {
        throw new Error('Login failed. Unable to determine user type.');
      }
      
      localStorage.setItem('token', response.data.id);
      localStorage.setItem('userType', resolvedType);
      localStorage.setItem('companyId', response.data.company_id);
      
      setUser(response.data);
      setUserType(resolvedType);
      setCompanyId(response.data.company_id);
      setError(null);
      return response.data;
    } catch (err) {
      console.error('Login failed:', err);
      const errorMessage = err.response?.data?.error || 'Login failed. Please try again.';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const register = async (userData, type = 'user') => {
    try {
      setLoading(true);
      const endpoint = type === 'user' ? 'auth/register' : 'employees';
      const response = await axios.post(`${API_URL}/${endpoint}`, userData);
      return response.data;
    } catch (err) {
      console.error('Registration failed:', err);
      const errorMessage = err.response?.data?.error || 'Registration failed. Please try again.';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      const token = localStorage.getItem('token');
      const type = localStorage.getItem('userType');
      const endpoint = type === 'user' ? 'auth/logout' : 'employees/logout';
      
      await axios.post(`${API_URL}/${endpoint}`, {}, {
        headers: {
          'user-id': token,
          'Authorization': `Bearer ${token}`
        }
      });
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      setUser(null);
      setUserType(null);
      setCompanyId(null);
      localStorage.removeItem('token');
      localStorage.removeItem('userType');
      localStorage.removeItem('companyId');
    }
  };

  const updateProfile = async (profileData) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const type = localStorage.getItem('userType');
      const endpoint = type === 'user' ? 'auth/profile' : 'employees';
      
      const response = await axios.put(
        type === 'user' 
          ? `${API_URL}/${endpoint}` 
          : `${API_URL}/${endpoint}/${user.id}`,
        profileData,
        {
          headers: {
            'user-id': token,
            'user-type': type,
            'Authorization': `Bearer ${token}`
          }
        }
      );
      
      setUser(response.data);
      return response.data;
    } catch (err) {
      console.error('Profile update failed:', err);
      const errorMessage = err.response?.data?.error || 'Failed to update profile. Please try again.';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const changePassword = async (currentPassword, newPassword) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const type = localStorage.getItem('userType');
      const endpoint = type === 'user' 
        ? 'auth/password' 
        : `employees/${user.id}/password`;

      const data = type === 'user' 
        ? { currentPassword, newPassword } 
        : { password: newPassword };

      const response = await axios.put(
        `${API_URL}/${endpoint}`, 
        data,
        {
          headers: {
            'user-id': token,
            'user-type': type,
            'Authorization': `Bearer ${token}`
          }
        }
      );
      return response.data;
    } catch (err) {
      console.error('Password change failed:', err);
      const errorMessage = err.response?.data?.error || 'Failed to change password. Please try again.';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

    
  return (
    <AuthContext.Provider value={{ 
      user, 
      userType,
      companyId,
      login, 
      register,
      logout, 
      updateProfile,
      changePassword,
      loading,
      error,
      setError
    }}>
      {!loading && children}
    </AuthContext.Provider>
  );
};